import { Dialog, DialogTrigger } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import Toggle from '~/components/inputs/Toggle/Toggle';
import { VStack } from '~/components/layout/Stack';
import Popover from '~/components/popovers/Popover/Popover';
import Text from '~/components/typographies/Text';
import { useAnalytics } from '~/hooks/useAnalytics';
import IconSettings from '~/icons/icon-settings.svg?react';
import { AnalyticsEvents } from '~/services/analytics';
import { useIsColumnVisible, useToggleColumn } from '~/state/tableColumns';
import { assertIsNotEmpty } from '~/types/typeGuards';
import { getToggleableColumns, type TableColumnVisibilityConfig } from './ColumnConfig';
import styles from './ColumnsPicker.module.scss';

interface Props {
  columns: readonly TableColumnVisibilityConfig[];
}

/**
 * Lets the user choose which optional columns of a table are shown. It reads and
 * writes the per-table visibility state provided by `TableColumnsProvider`, so
 * any table wrapped in `TableProvider` can drop this in next to its filters.
 */
const ColumnsPicker = ({ columns }: Props) => {
  const { t } = useTranslation('inputs', { keyPrefix: 'columns_picker' });
  const isVisible = useIsColumnVisible();
  const toggleColumn = useToggleColumn();
  const { track } = useAnalytics();

  const toggleableColumns = getToggleableColumns(columns);

  if (toggleableColumns.length === 0) {
    return null;
  }

  return (
    <DialogTrigger
      onOpenChange={(isOpen) => {
        if (isOpen) {
          track(AnalyticsEvents.COLUMN_PICKER_CLICKED);
        }
      }}
    >
      <IconButton Icon={IconSettings} variant="ghost" size="small" label={t('label')} className={styles.trigger} />
      <Popover placement="bottom end">
        <Dialog aria-label={t('label')}>
          <VStack gap={2} className={styles.content}>
            <Text variant="body2" weight="semibold">
              {t('title')}
            </Text>
            {toggleableColumns.map((column) => {
              const label = column.pickerLabel ?? column.label;
              assertIsNotEmpty(label, `Toggleable column "${column.id}" requires a non-empty label or pickerLabel`);

              return (
                <Toggle
                  key={column.id}
                  isSelected={isVisible(column.id)}
                  onChange={() => toggleColumn(column.id)}
                  label={label}
                />
              );
            })}
          </VStack>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};

export default ColumnsPicker;
